import {
    ChangeDetectionStrategy,
    Component,
    booleanAttribute,
    computed,
    input,
} from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import type { Maybe } from '@quipex/core/primitives';
import {
    resolveColorContext,
    getHashListItem,
    type ContextFamily,
    type ContextSelection,
    type ContextTone,
} from '@quipex/shared/utils';
import { QpxIcon } from '../../directives';

const AVATAR_COLOR_PALETTE = [
    { family: 'primary', tone: 'muted' },
    { family: 'success', tone: 'muted' },
    { family: 'warning', tone: 'muted' },
    { family: 'info', tone: 'muted' },
    { family: 'caution', tone: 'muted' },
    { family: 'destructive', tone: 'muted' },
    { family: 'urgent', tone: 'muted' },
] as const satisfies readonly ContextSelection[];

export function getAvatarColorClasses(value: string): {
    bg: string;
    text: string;
} {
    const context = getHashedColorContext(value);
    const colors = resolveColorContext(context.family, context.tone);
    return {
        bg: colors.surface.className,
        text: colors.decorative.className,
    };
}

export function getHashedColorContext(value: string): ContextSelection {
    return getHashListItem(value, AVATAR_COLOR_PALETTE);
}

@Component({
    selector: 'qpx-avatar',
    imports: [NgIcon, QpxIcon],
    templateUrl: './avatar.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class]': 'hostClass()',
        'aria-hidden': 'true',
        '[class.mat-icon]': 'matButtonIcon()',
        '[class.darken-background]': 'darkenBackground()',
        '[style.--qpx-avatar-bg]': 'containerBgVar()',
    },
})
export class QpxAvatar {
    //#region Inputs
    /**
     * Stable key used for deterministic avatar color selection.
     * Prefer an immutable identifier (for example, user ID) so color does not
     * change when display values such as `name` are updated.
     */
    public readonly colorKey = input<Maybe<string>>();

    /**
     * Optional image source. When provided, the image is rendered and takes
     * precedence over initials and fallback icon.
     */
    public readonly imageUrl = input<Maybe<string>>();

    /**
     * Optional explicit initials (up to 3 characters for md, 2 for sm/xs).
     * Used only when `imageUrl` is not provided.
     */
    public readonly initials = input<Maybe<string>>();

    /**
     * Optional display name used to derive initials (up to 3 letters for md, 2 for sm/xs) when
     * explicit `initials` are not provided.
     */
    public readonly name = input<Maybe<string>>();

    /**
     * Lucide icon name rendered when no `imageUrl`, `initials`, or `name`
     * can produce visible avatar content.
     */
    public readonly fallbackIcon = input<string, Maybe<string>>(
        'lucideCircleHelp',
        {
            transform: (value: Maybe<string>) =>
                value?.trim() || 'lucideCircleHelp',
        }
    );

    /**
     * Avatar dimensions: md 40px, sm 32px, xs 24px.
     */
    public readonly size = input<'md' | 'sm' | 'xs'>('md');

    /** Opts into Material button icon placement without changing avatar size. */
    public readonly matButtonIcon = input(false, {
        transform: booleanAttribute,
    });

    /**
     * Optional explicit color context. When provided, it overrides the
     * deterministic hashed avatar palette.
     */
    public readonly colorContext = input<Maybe<ContextFamily>>();
    public readonly tone = input<ContextTone>('default');

    /** Strengthens the surface using the same inset treatment as hero icons. */
    public readonly darkenBackground = input(false);
    //#endregion

    //#region State
    private readonly resolvedColorContext = computed(() => {
        const explicitContext = this.colorContext();
        if (explicitContext) {
            return resolveColorContext(explicitContext, this.tone());
        }

        const key =
            this.normalized(this.colorKey()) ||
            this.normalized(this.name()) ||
            this.normalized(this.initials()) ||
            this.fallbackIcon();
        const context = getHashedColorContext(key);
        return resolveColorContext(context.family, context.tone);
    });

    protected readonly avatarClasses = computed(() => ({
        bg: this.resolvedColorContext().surface.className,
        text: this.resolvedColorContext().decorative.className,
    }));

    protected readonly containerBgVar = computed(
        () => `var(${this.resolvedColorContext().surface.variable})`
    );

    protected readonly hostClass = computed(() => {
        return `qpx-avatar ${this.size()} ${this.avatarClasses().bg} ${this.avatarClasses().text}`;
    });

    protected readonly initialsClass = computed(() => {
        return `avatar ${this.avatarClasses().text}`;
    });

    protected readonly resolvedImageUrl = computed(() => {
        return this.normalized(this.imageUrl());
    });

    protected readonly resolvedInitials = computed(() => {
        const explicitInitials = this.normalizeInitials(this.initials());
        if (explicitInitials) {
            return explicitInitials;
        }

        return this.initialsFromName(this.name());
    });
    //#endregion

    //#region Private methods
    private normalized(value: Maybe<string>): string {
        return value?.trim() ?? '';
    }

    private normalizeInitials(value: Maybe<string>): string {
        const initials = this.normalized(value).replace(/\s+/g, '');
        return initials.slice(0, this.size() === 'md' ? 3 : 2).toUpperCase();
    }

    private initialsFromName(value: Maybe<string>): string {
        const normalizedName = this.normalized(value);
        if (!normalizedName) {
            return '';
        }

        const words = normalizedName.split(/\s+/).filter(Boolean);
        return words
            .slice(0, this.size() === 'md' ? 3 : 2)
            .map((word) => word[0] ?? '')
            .join('')
            .toUpperCase();
    }
    //#endregion
}
